import { useCallback, useEffect, useMemo, useState } from 'react'
import { Plus, Save, Search, Trash2 } from 'lucide-react'
import { useModalA11y } from '../hooks/useModalA11y'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useToast } from '../contexts/ToastContext'
import { useCan } from '../hooks/useCan'
import WeightRatesCard, { type WeightRate } from '../components/services/WeightRatesCard'

type ServiceItem = {
  id: string
  service_id: string
  name: string
  price: number
  unit?: string | null
  category?: string | null
  is_active?: boolean
}

// Sub-category the customer app uses to group items into tabs. The `value` must
// match the keys the app expects (ServiceDetailScreen CATEGORY_ORDER); '' means
// uncategorized (item shows without a Men/Women/Kids tab).
const CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Uncategorized' },
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
  { value: 'kids', label: 'Kids' },
  { value: 'household', label: 'Home' },
  { value: 'misc', label: 'More' },
]
const categoryLabel = (value?: string | null) =>
  CATEGORY_OPTIONS.find((o) => o.value === (value || ''))?.label ?? value ?? 'Uncategorized'

type Service = {
  id: string
  name: string
  /** Internal identifier. Never shown to a customer or on an order. */
  slug?: string | null
  description?: string | null
  base_price?: number | null
  price_unit?: string | null
  delivery_model?: 'standard' | 'express'
  is_active?: boolean
  display_order?: number | null
  items: ServiceItem[]
}

/**
 * How a line is priced, in words. `per_kg` / `per_item` are the stored values;
 * showing them raw made the admin read a database enum to answer "is this
 * charged by weight or by piece".
 */
const pricingModelLabel = (unit?: string | null): string => {
  const value = String(unit || '').toLowerCase()
  if (value === 'per_kg') return 'Per kg'
  if (value === 'per_item') return 'Per item'
  if (!value) return 'Per item'
  return value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

const PAGE_SIZE = 12

export default function ServicePricingPage() {
  const colors = useColors()
  const { addToast } = useToast()
  const can = useCan()
  // Same permission the backend enforces on the pricing routes - no new
  // permission, and the UI never offers an edit the API would reject.
  const canUpdatePricing = can('services.update')

  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')

  const [sortBy, setSortBy] = useState<'name' | 'price'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)

  const [draftPrices, setDraftPrices] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const [showCreateService, setShowCreateService] = useState(false)
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    base_price: '',
    price_unit: 'per_item',
    delivery_model: 'standard' as 'standard' | 'express',
  })

  const [showCreateItem, setShowCreateItem] = useState(false)
  const [newItem, setNewItem] = useState({ name: '', price: '', unit: 'per_item', category: '' })
  const [serviceModalError, setServiceModalError] = useState('')
  const [itemModalError, setItemModalError] = useState('')
  const [serviceSaving, setServiceSaving] = useState(false)
  const [itemSaving, setItemSaving] = useState(false)
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null)
  const [togglingItemId, setTogglingItemId] = useState<string | null>(null)
  const serviceModalRef = useModalA11y(showCreateService, () => { setShowCreateService(false); setServiceModalError('') })
  const itemModalRef = useModalA11y(showCreateItem, () => { setShowCreateItem(false); setItemModalError('') })

  const loadServices = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const response = await adminApi.getServicesFull()
      const list = response?.data || response || []
      const normalized: Service[] = Array.isArray(list) ? list : []
      setServices(normalized)
      if (!selectedServiceId && normalized[0]?.id) setSelectedServiceId(normalized[0].id)
    } catch {
      setError('Unable to load service pricing.')
      setServices([])
    } finally {
      setLoading(false)
    }
  }, [selectedServiceId])

  useEffect(() => {
    void loadServices()
  }, [loadServices])

  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServiceId) || null,
    [services, selectedServiceId]
  )

  /**
   * Every per-kg rate in the catalog, surfaced up front so an admin can change
   * "Wash & Fold - By weight - ₹120/kg" without hunting through a garment list.
   * Read from the SAME services payload the rest of the page uses; the row is a
   * real `service_items` row with unit 'per_kg'.
   */
  const weightRates = useMemo<WeightRate[]>(
    () =>
      services.flatMap((service) =>
        (service.items || [])
          .filter((item) => String(item.unit || '').toLowerCase() === 'per_kg')
          .map((item) => ({
            serviceId: service.id,
            serviceName: service.name,
            itemId: item.id,
            itemName: item.name,
            price: Number(item.price || 0),
            isActive: item.is_active !== false && service.is_active !== false,
          }))
      ),
    [services]
  )

  const onSaveWeightRate = useCallback(
    async (rate: WeightRate, newPrice: number) => {
      try {
        // Reuses the existing catalog endpoint. The backend also re-points the
        // service's advertised "FROM ₹X/KG" base price at the cheapest active
        // per-kg rate, so the customer app never advertises a stale figure.
        await adminApi.updateServiceItem(rate.serviceId, rate.itemId, { price: newPrice })
        addToast(
          `${rate.serviceName} · ${rate.itemName} saved at ₹${newPrice}/kg, applies to new orders`,
          'success'
        )
        await loadServices()
      } catch (err) {
        addToast(
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message
            || 'Failed to save the rate.',
          'error'
        )
        throw err
      }
    },
    [addToast, loadServices]
  )

  const filteredItems = useMemo(() => {
    const source = selectedService?.items || []
    const needle = search.trim().toLowerCase()
    const next = source.filter((item) => {
      if (statusFilter === 'active' && !item.is_active) return false
      if (statusFilter === 'inactive' && item.is_active) return false
      if (!needle) return true
      return item.name.toLowerCase().includes(needle)
    })

    next.sort((a, b) => {
      if (sortBy === 'name') {
        const compare = a.name.localeCompare(b.name)
        return sortOrder === 'asc' ? compare : -compare
      }
      const compare = Number(a.price || 0) - Number(b.price || 0)
      return sortOrder === 'asc' ? compare : -compare
    })

    return next
  }, [selectedService, search, statusFilter, sortBy, sortOrder])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE))
  const pagedItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const hasPriceChanges = useMemo(() => {
    return Object.keys(draftPrices).some((itemId) => {
      const item = selectedService?.items.find((line) => line.id === itemId)
      if (!item) return false
      return Number(draftPrices[itemId]) !== Number(item.price)
    })
  }, [draftPrices, selectedService])

  const onSaveChanges = async () => {
    if (!selectedService || !hasPriceChanges) return
    if (!window.confirm('Are you sure you want to update prices? This affects all new orders.')) return

    try {
      setSaving(true)
      const changed = Object.entries(draftPrices).filter(([itemId, value]) => {
        const current = selectedService.items.find((line) => line.id === itemId)
        return current && Number(value) !== Number(current.price)
      })

      await Promise.all(
        changed.map(([itemId, value]) =>
          adminApi.updateServiceItem(selectedService.id, itemId, { price: Number(value) })
        )
      )

      addToast('Pricing updated successfully', 'success')
      setDraftPrices({})
      await loadServices()
    } catch {
      addToast('Failed to update pricing', 'error')
    } finally {
      setSaving(false)
    }
  }

  const onToggleService = async (service: Service) => {
    try {
      await adminApi.toggleServiceActive(service.id, !service.is_active)
      await loadServices()
      addToast(`Service ${!service.is_active ? 'activated' : 'deactivated'}`, 'success')
    } catch {
      addToast('Failed to update service status', 'error')
    }
  }

  const onCreateService = async () => {
    if (!newService.name.trim()) {
      setServiceModalError('Service name is required.')
      return
    }
    const basePrice = newService.base_price ? Number(newService.base_price) : 0
    if (!Number.isFinite(basePrice) || basePrice < 0) {
      setServiceModalError('Base price cannot be negative.')
      return
    }
    setServiceSaving(true)
    setServiceModalError('')
    try {
      await adminApi.createService({
        name: newService.name.trim(),
        description: newService.description.trim() || undefined,
        base_price: basePrice,
        price_unit: newService.price_unit,
        delivery_model: newService.delivery_model,
      })
      setShowCreateService(false)
      setNewService({ name: '', description: '', base_price: '', price_unit: 'per_item', delivery_model: 'standard' })
      await loadServices()
      addToast('Service created', 'success')
    } catch (err) {
      setServiceModalError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to create service.'
      )
    } finally {
      setServiceSaving(false)
    }
  }

  const onCreateItem = async () => {
    if (!selectedService || !newItem.name.trim() || !newItem.price) {
      setItemModalError('Item name and price are required.')
      return
    }
    const price = Number(newItem.price)
    if (!Number.isFinite(price) || price < 0) {
      setItemModalError('Price cannot be negative.')
      return
    }
    setItemSaving(true)
    setItemModalError('')
    try {
      await adminApi.createServiceItem(selectedService.id, {
        name: newItem.name.trim(),
        price,
        unit: newItem.unit,
        category: newItem.category || null,
      })
      setShowCreateItem(false)
      setNewItem({ name: '', price: '', unit: 'per_item', category: '' })
      await loadServices()
      addToast('Service item created', 'success')
    } catch (err) {
      setItemModalError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to create service item.'
      )
    } finally {
      setItemSaving(false)
    }
  }

  const onToggleItem = async (item: ServiceItem) => {
    if (!selectedService) return
    setTogglingItemId(item.id)
    try {
      // Item-level visibility. The customer app's GET /services/:id/items only
      // returns is_active=true rows, so toggling this shows/hides the item (and
      // its price) in the app with no app release.
      await adminApi.updateServiceItem(selectedService.id, item.id, { is_active: !item.is_active })
      await loadServices()
      addToast(`Item ${!item.is_active ? 'activated' : 'deactivated'}`, 'success')
    } catch (err) {
      addToast(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to update item status.',
        'error'
      )
    } finally {
      setTogglingItemId(null)
    }
  }

  const onSetItemCategory = async (item: ServiceItem, category: string) => {
    if (!selectedService || (item.category || '') === category) return
    setTogglingItemId(item.id)
    try {
      // Drives the Men/Women/Kids/Home tabs in the customer app. Saves
      // immediately; the app picks it up on its next catalog fetch - no release.
      await adminApi.updateServiceItem(selectedService.id, item.id, { category: category || null })
      await loadServices()
      addToast(`Category set to ${categoryLabel(category)}`, 'success')
    } catch (err) {
      addToast(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to update category.',
        'error'
      )
    } finally {
      setTogglingItemId(null)
    }
  }

  const onDeleteItem = async (item: ServiceItem) => {
    if (!selectedService) return
    if (!window.confirm(`Delete "${item.name}"? This removes it from the catalog and the order wizard.`)) return
    setDeletingItemId(item.id)
    try {
      await adminApi.deleteServiceItem(selectedService.id, item.id)
      await loadServices()
      addToast('Service item deleted', 'success')
    } catch (err) {
      addToast(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to delete item.',
        'error'
      )
    } finally {
      setDeletingItemId(null)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading services...</div>
  if (error) return <div style={{ color: '#991B1B' }}>{error}</div>
  if (!services.length) return <div style={{ color: colors.textMuted }}>No services found.</div>

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 className="font-display" style={{ margin: 0, color: colors.textPrimary }}>Service Pricing</h2>
          <p className="font-body" style={{ margin: '4px 0 0', color: colors.textMuted }}>Manage services, item pricing, and availability</p>
        </div>
        <button onClick={() => { setServiceModalError(''); setShowCreateService(true) }} style={{ border: 0, borderRadius: 8, background: colors.accent, color: colors.bgSidebar, padding: '8px 12px', fontWeight: 700 }}>
          <Plus size={14} style={{ marginRight: 6 }} /> Add service
        </button>
      </div>

      <WeightRatesCard rates={weightRates} canEdit={canUpdatePricing} onSave={onSaveWeightRate} />

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 14 }}>
        <aside style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 10, maxHeight: '70vh', overflowY: 'auto' }}>
          {services.map((service) => (
            <div key={service.id} style={{ border: `1px solid ${selectedServiceId === service.id ? colors.accent : colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <button onClick={() => { setSelectedServiceId(service.id); setDraftPrices({}); setPage(1) }} style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}>
                <div style={{ color: colors.textPrimary, fontWeight: 700 }}>{service.name}</div>
                <div style={{ color: colors.textMuted, fontSize: 12 }}>{service.description || '-'}</div>
                {/* The canonical definition behind the display name. The slug is
                    the INTERNAL identifier - shown here, where services are
                    administered, and nowhere a customer or an order can see it. */}
                <div style={{ color: colors.textMuted, fontSize: 11, marginTop: 4 }}>
                  {service.slug ? <code style={{ fontSize: 10 }}>{service.slug}</code> : null}
                  {service.slug ? ' · ' : ''}
                  {pricingModelLabel(service.price_unit)}
                  {service.base_price != null ? ` · from ₹${Number(service.base_price).toFixed(2)}` : ''}
                </div>
              </button>
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: colors.textMuted }}>{service.is_active ? 'Active' : 'Inactive'}</span>
                <button onClick={() => void onToggleService(service)} style={{ border: 0, borderRadius: 999, padding: '4px 10px', background: service.is_active ? '#DCFCE7' : '#FEE2E2', color: service.is_active ? '#166534' : '#991B1B' }}>
                  {service.is_active ? 'Disable' : 'Enable'}
                </button>
              </div>
            </div>
          ))}
        </aside>

        <section style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, color: colors.textPrimary }}>{selectedService?.name}</h3>
            <button onClick={() => { setItemModalError(''); setShowCreateItem(true) }} style={{ border: 0, borderRadius: 8, background: '#0F2E2A', color: '#F3EFE6', padding: '8px 12px' }}>
              <Plus size={14} style={{ marginRight: 6 }} /> Add item
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ position: 'relative', minWidth: 280 }}>
              <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: colors.textMuted }} />
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} placeholder="Search items" style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: `1px solid ${colors.border}` }} />
            </div>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as 'all' | 'active' | 'inactive'); setPage(1) }} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}` }}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'name' | 'price')} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}` }}>
              <option value="name">Sort: Item name</option>
              <option value="price">Sort: Price</option>
            </select>
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}` }}>
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </div>

          {!filteredItems.length ? (
            <div style={{ color: colors.textMuted, padding: 18 }}>No service items found.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: colors.bgSecondary }}>
                      {['Item Name', 'Current Price', 'New Price', 'Pricing model', 'Category', 'Status', ''].map((head, i) => (
                        <th key={head || `col-${i}`} style={{ textAlign: 'left', padding: 10, fontSize: 12, color: colors.textMuted }}>{head}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedItems.map((item) => (
                      <tr key={item.id} style={{ borderTop: `1px solid ${colors.border}`, opacity: item.is_active === false ? 0.55 : 1 }}>
                        <td style={{ padding: 10 }}>{item.name}</td>
                        <td style={{ padding: 10 }}>₹{Number(item.price).toFixed(2)}</td>
                        <td style={{ padding: 10 }}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={draftPrices[item.id] ?? String(item.price)}
                            onChange={(e) => setDraftPrices((prev) => ({ ...prev, [item.id]: e.target.value }))}
                            style={{ width: 120, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 6 }}
                          />
                        </td>
                        <td style={{ padding: 10 }}>{pricingModelLabel(item.unit)}</td>
                        <td style={{ padding: 10 }}>
                          <select
                            value={item.category || ''}
                            disabled={togglingItemId === item.id}
                            onChange={(e) => void onSetItemCategory(item, e.target.value)}
                            aria-label={`Category for ${item.name}`}
                            style={{ padding: '6px 8px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.textPrimary, cursor: togglingItemId === item.id ? 'not-allowed' : 'pointer' }}
                          >
                            {CATEGORY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: 10 }}>
                          <button
                            type="button"
                            aria-label={`${item.is_active === false ? 'Activate' : 'Deactivate'} ${item.name}`}
                            disabled={togglingItemId === item.id}
                            onClick={() => void onToggleItem(item)}
                            style={{ border: 0, borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600, background: item.is_active === false ? '#FEE2E2' : '#DCFCE7', color: item.is_active === false ? '#991B1B' : '#166534', cursor: togglingItemId === item.id ? 'not-allowed' : 'pointer', opacity: togglingItemId === item.id ? 0.6 : 1 }}
                          >
                            {item.is_active === false ? 'Inactive' : 'Active'}
                          </button>
                        </td>
                        <td style={{ padding: 10, textAlign: 'right' }}>
                          <button
                            type="button"
                            aria-label={`Delete ${item.name}`}
                            disabled={deletingItemId === item.id}
                            onClick={() => void onDeleteItem(item)}
                            style={{ border: `1px solid ${colors.border}`, borderRadius: 8, background: 'transparent', color: '#B91C1C', padding: '4px 8px', cursor: deletingItemId === item.id ? 'not-allowed' : 'pointer', opacity: deletingItemId === item.id ? 0.5 : 1 }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: colors.textMuted, fontSize: 12 }}>
                  Showing {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, filteredItems.length)} of {filteredItems.length}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
                  <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
                </div>
              </div>
            </>
          )}

          <div style={{ marginTop: 14 }}>
            <button disabled={!hasPriceChanges || saving || !canUpdatePricing} onClick={() => void onSaveChanges()} style={{ border: 0, borderRadius: 8, background: hasPriceChanges && canUpdatePricing ? '#0F2E2A' : '#9CA3AF', color: '#F3EFE6', padding: '10px 14px' }}>
              <Save size={14} style={{ marginRight: 6 }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </section>
      </div>

      {showCreateService ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'grid', placeItems: 'center', zIndex: 60 }}>
          <div ref={serviceModalRef} role="dialog" aria-modal="true" style={{ width: 480, background: colors.bgCard, borderRadius: 12, padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Create service</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              <input placeholder="Name" value={newService.name} onChange={(e) => setNewService((p) => ({ ...p, name: e.target.value }))} />
              <input placeholder="Description" value={newService.description} onChange={(e) => setNewService((p) => ({ ...p, description: e.target.value }))} />
              <input type="number" min="0" step="0.01" placeholder="Base price" value={newService.base_price} onChange={(e) => setNewService((p) => ({ ...p, base_price: e.target.value }))} />
              <input placeholder="Price unit" value={newService.price_unit} onChange={(e) => setNewService((p) => ({ ...p, price_unit: e.target.value }))} />
              <select value={newService.delivery_model} onChange={(e) => setNewService((p) => ({ ...p, delivery_model: e.target.value as 'standard' | 'express' }))}>
                <option value="standard">standard</option>
                <option value="express">express</option>
              </select>
            </div>
            {serviceModalError ? (
              <p className="font-body" style={{ margin: '10px 0 0', fontSize: 13, color: '#B91C1C' }}>{serviceModalError}</p>
            ) : null}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => { setShowCreateService(false); setServiceModalError('') }}>Cancel</button>
              <button onClick={() => void onCreateService()} disabled={serviceSaving} style={{ background: colors.accent, border: 0, borderRadius: 8, padding: '8px 12px', color: colors.bgSidebar, fontWeight: 700, opacity: serviceSaving ? 0.6 : 1, cursor: serviceSaving ? 'not-allowed' : 'pointer' }}>{serviceSaving ? 'Creating…' : 'Create'}</button>
            </div>
          </div>
        </div>
      ) : null}

      {showCreateItem ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'grid', placeItems: 'center', zIndex: 60 }}>
          <div ref={itemModalRef} role="dialog" aria-modal="true" style={{ width: 420, background: colors.bgCard, borderRadius: 12, padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Add service item</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              <input placeholder="Item name" value={newItem.name} onChange={(e) => setNewItem((p) => ({ ...p, name: e.target.value }))} />
              <input type="number" min="0" step="0.01" placeholder="Price" value={newItem.price} onChange={(e) => setNewItem((p) => ({ ...p, price: e.target.value }))} />
              <input placeholder="Unit" value={newItem.unit} onChange={(e) => setNewItem((p) => ({ ...p, unit: e.target.value }))} />
              <label style={{ fontSize: 12, color: colors.textMuted }}>
                Category (tab in the app)
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem((p) => ({ ...p, category: e.target.value }))}
                  style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.textPrimary }}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
            </div>
            {itemModalError ? (
              <p className="font-body" style={{ margin: '10px 0 0', fontSize: 13, color: '#B91C1C' }}>{itemModalError}</p>
            ) : null}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => { setShowCreateItem(false); setItemModalError('') }}>Cancel</button>
              <button onClick={() => void onCreateItem()} disabled={itemSaving} style={{ background: '#0F2E2A', border: 0, borderRadius: 8, padding: '8px 12px', color: '#fff', opacity: itemSaving ? 0.6 : 1, cursor: itemSaving ? 'not-allowed' : 'pointer' }}>{itemSaving ? 'Adding…' : 'Add'}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
